import React, { useEffect, useState } from 'react';
import { getConsent, saveConsent } from '../utils/cookieConsent';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledCookieBanner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(90deg, #CE18BC, #7531FC);
  color: #fff;
  padding: 16px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;

  .cookie-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
    min-height: 160px;
  }

  .cookie-text {
    font-size: 14px;
    line-height: 1.5;
    min-height: 72px;
  }

  .cookie-text strong {
    font-size: 16px;
    font-weight: 700;
  }

  .cookie-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.3s;
  }

  .btn-primary {
    background: #fff;
    color: #7531FC;
    font-weight: 700;
  }

  .btn-primary:hover {
    background: #E9E7FB;
  }

  .btn-secondary {
    background: #7531FC;
    color: #fff;
  }

  .btn-secondary:hover {
    background: #CE18BC;
  }

  .btn-minimal {
    background: transparent;
    color: #fff;
  }

  .btn-minimal:hover {
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    .cookie-content {
      padding: 8px;
    }

    .btn {
      font-size: 12px;
      padding: 6px 12px;
    }
  }
`;

const getInitialConsentState = () => {
  try {
    return getConsent();
  } catch {
    return null;
  }
};

export default function CookieConsent() {
  const storedConsent = getInitialConsentState();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => !storedConsent);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [consent, setConsent] = useState(() => storedConsent || { necessary: true, analytics: false });

  useEffect(() => {
    // if consent changes elsewhere (e.g. storage cleared) keep banner accurate
    function handleStorage(e) {
      if (e.key === 'planiaro-cookie-consent') {
        const latest = getInitialConsentState();
        setVisible(!latest);
        setConsent(latest || { necessary: true, analytics: false });
      }
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  function saveAndClose(obj) {
    saveConsent(obj);
    setVisible(false);
    setPrefsOpen(false);
    window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: obj }));
  }

  function acceptAll() { saveAndClose({ necessary: true, analytics: true }); }
  function acceptNecessary() { saveAndClose({ necessary: true, analytics: false }); }

  if (!visible) return null;

  // Use current language for the privacy-policy link so it resolves correctly
  const currentLang = (typeof window !== 'undefined' && window.document.documentElement.lang) || 'es';

  return (
    <>
      <StyledCookieBanner 
        role="dialog" 
        aria-live="polite"
        aria-labelledby="cookie-dialog-title"
        aria-modal="true"
      >
        <div className="cookie-content">
          <div className="cookie-text">
            <strong id="cookie-dialog-title">{t('cookie_titulo', 'Usamos cookies')}</strong>
            <div>{t('cookie_descripcion', 'Utilizamos cookies para mejorar la experiencia y, con tu permiso, para analizar el uso (analytics).')}</div>
            <a className="cookie-link" href={`/${currentLang}/privacy-policy`} style={{ color: '#fff', textDecoration: 'underline' }}>{t('cookie_politica', 'Ver política de privacidad')}</a>
          </div>

          <div className="cookie-actions">
            <button className="btn btn-secondary" onClick={() => setPrefsOpen(true)}>{t('cookie_preferencias', 'Preferencias')}</button>
            <button className="btn btn-minimal" onClick={acceptNecessary}>{t('cookie_solo_necesarias', 'Solo necesarias')}</button>
            <button className="btn btn-primary" onClick={acceptAll}>{t('cookie_aceptar_todo', 'Aceptar todo')}</button>
          </div>
        </div>
      </StyledCookieBanner>

      {prefsOpen && (
        <div 
          className="cookie-preferences" 
          role="dialog"
          aria-labelledby="cookie-prefs-title"
          aria-modal="true"
          style={{
            position: 'fixed',
            bottom: '20%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            color: '#000',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1001
          }}>
          <h2 id="cookie-prefs-title">{t('cookie_prefs_titulo', 'Preferencias de cookies')}</h2>
          <p>{t('cookie_prefs_descripcion', 'Selecciona las cookies que deseas permitir:')}</p>
          <label>
            <input
              type="checkbox"
              checked={consent.analytics}
              onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
            />
            {t('cookie_analytics', 'Cookies de análisis')}
          </label>
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={() => saveAndClose(consent)}>{t('cookie_guardar_prefs', 'Guardar preferencias')}</button>
            <button className="btn btn-secondary" onClick={() => setPrefsOpen(false)}>{t('btn_cancelar', 'Cancelar')}</button>
          </div>
        </div>
      )}
    </>
  );
}
